/**
 * Simple Date Range Picker component
 * 
 * Uses native date inputs for simplicity.
 */

import { useMemo } from 'react';
import type { DateRange } from '../types';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}

const PRESET_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'This month', days: -1 }, // Special case
  { label: 'Last month', days: -2 }, // Special case
];

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

export function DateRangePicker({ value, onChange, className = '' }: DateRangePickerProps) {
  const fromValue = useMemo(() => formatDateForInput(value.from), [value.from]);
  const toValue = useMemo(() => formatDateForInput(value.to), [value.to]);

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFrom = new Date(e.target.value);
    if (!isNaN(newFrom.getTime())) {
      onChange({ from: newFrom, to: value.to });
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTo = new Date(e.target.value);
    if (!isNaN(newTo.getTime())) {
      onChange({ from: value.from, to: newTo });
    }
  };

  const handlePresetClick = (preset: { days: number }) => {
    const now = new Date();
    let from: Date;
    let to: Date = now;

    if (preset.days === -1) {
      // This month
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (preset.days === -2) {
      // Last month
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to = new Date(now.getFullYear(), now.getMonth(), 0);
    } else {
      from = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
    }

    onChange({ from, to });
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Preset buttons */}
      <div className="flex flex-wrap gap-1">
        {PRESET_RANGES.map((preset) => (
          <button
            key={preset.label}
            onClick={() => handlePresetClick(preset)}
            className="px-2 py-1 text-xs rounded-md bg-muted hover:bg-accent transition-colors"
          >
            {preset.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={fromValue}
          onChange={handleFromChange}
          className="px-2 py-1 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <span className="text-muted-foreground">to</span>
        <input
          type="date"
          value={toValue}
          onChange={handleToChange}
          className="px-2 py-1 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
    </div>
  );
}
